/**
 * Which workspace a signed-in account is standing in, and what to say when
 * there is none: the chooser, the denied and unprovisioned states, and the
 * resolution of `/:role/:org/:destination` against what the account holds.
 *
 * The URL is a question and never an assertion. The set of workspaces comes
 * from the server, derived from real grants, so a stale bookmark or a
 * hand-edited path resolves to the chooser rather than to an error. Once a
 * workspace is open, the portal's own frame takes over — `shell.model.ts`
 * parses the destination, `portals.ts` says which screen a role opens — and
 * this file has nothing more to say. The words are `workspace.constants.ts`'s.
 *
 * Choosing is not state. The chooser navigates by link and this file reads
 * the address back, so the open workspace and the address bar are one fact.
 */

import type {
  ClaimableStudio,
  CollectionSummary,
  DeclaredMemberType,
  MeResponse,
  Membership,
  MySeatInvitation,
  RoleKey,
  WorkspaceRef,
} from "@ondo/shared";

import { MEMBERSHIP_TYPES } from "../lib/constants.ts";
import { plural } from "../lib/text.ts";
import type { MeState } from "../lib/useMe.ts";
import { portalFor } from "../shell/portals.ts";
import { parseDestination, parseRecord, recordRule } from "../shell/shell.model.ts";
import { PATHS } from "../routes/routes.constants.ts";
import { matchWorkspace, workspacePath } from "../routes/routes.model.ts";
import {
  CLAIM_STUDIO_COPY,
  EMPTY_CHOOSER_COPY,
  SEAT_INVITATION_COPY,
  SEAT_INVITATION_RUNGS,
  WORKSPACE_COPY,
  WORKSPACE_LABELS,
  WORKSPACE_NOUNS,
} from "./workspace.constants.ts";

/** One workspace as the chooser renders it, with every label already decided. */
export interface WorkspaceChoice {
  key: string;
  /** The organisation's own name if it has one — that is what the user recognises. */
  name: string;
  /** "Gallery · Senior Advisor". */
  meta: string;
  workspace: WorkspaceRef;
}

/**
 * What the workspace screen shows.
 *
 * A discriminated union rather than a set of flags, so "loading and also
 * denied" cannot be represented. Each variant carries exactly what its branch
 * of the view renders and nothing else.
 */
export type WorkspaceView =
  | { kind: "loading"; message: string }
  | { kind: "error"; title: string; message: string; unreachable: boolean }
  | { kind: "unprovisioned"; title: string; message: string }
  /** Signed in via a role this account does not hold. */
  | {
      kind: "denied";
      title: string;
      message: string;
      principalName: string;
      choices: WorkspaceChoice[];
      /** The account itself, for the door an empty chooser offers (OND-12). */
      me: MeResponse;
    }
  | {
      kind: "choosing";
      title: string;
      principalName: string;
      choices: WorkspaceChoice[];
      /** The account itself, for the door an empty chooser offers (OND-12). */
      me: MeResponse;
    }
  | {
      kind: "open";
      workspace: WorkspaceRef;
      principal: MeResponse["principal"];
      capabilities: MeResponse["capabilities"];
      /** "Hauser & Wirth", or the role label for a personal workspace. */
      title: string;
      /** "Gallery · Senior Advisor · signed in as Michael Hue-Williams (ONDO-1001)". */
      meta: string;
      canSwitch: boolean;
      /** Which screen this role opens. Only the collector has one built. */
      screen: WorkspaceScreen;
    };

export type WorkspaceScreen =
  | "collector"
  | "agent"
  | "gallery"
  | "verifier"
  | "estate"
  | "placeholder";

/**
 * Resolves which workspace to open, and what to say when there isn't one.
 *
 * The sign-in button the user clicked is a hint, never a claim: the set of
 * workspaces comes from the server, derived from real grants. Signing in via
 * "Agent" without an agent grant is not an authentication failure — the account
 * is valid, it just has nowhere by that name to go. So the caller is let in and
 * told plainly what they do and do not have.
 *
 * IT NO LONGER TAKES A `chosen` WORKSPACE, and the reason is worth keeping.
 * Choosing used to be state: the chooser held the picked workspace and handed
 * it back here, which meant the open workspace and the address bar were two
 * facts that could disagree. `WorkspaceChooser` now navigates by `<Link>`, so
 * the URL IS the choice and `resolveWorkspaceRoute` reads it back — one source,
 * and a bookmark that means what it says. The parameter outlived
 * `useWorkspaceController`, which was this function's only caller that ever
 * passed one, and survived purely because a test still exercised it.
 */
export function resolveWorkspace(input: {
  me: MeState;
  intended: RoleKey | null;
}): WorkspaceView {
  const { me, intended } = input;

  if (me.status === "loading") {
    return { kind: "loading", message: WORKSPACE_COPY.loading };
  }

  if (me.status === "error") {
    return {
      kind: "error",
      title: me.unreachable ? WORKSPACE_COPY.unreachableTitle : WORKSPACE_COPY.errorTitle,
      message: me.message,
      unreachable: me.unreachable,
    };
  }

  if (me.status === "unprovisioned") {
    return {
      kind: "unprovisioned",
      title: WORKSPACE_COPY.unprovisionedTitle,
      message: WORKSPACE_COPY.unprovisioned,
    };
  }

  const { workspaces, principal, capabilities } = me.me;
  const matched = intended
    ? workspaces.find((workspace) => workspace.role === intended)
    : undefined;
  const active = matched;
  const choices = workspaces.map(toChoice);

  if (!active && intended) {
    return {
      kind: "denied",
      title: `You don’t have ${WORKSPACE_LABELS[intended]} access`,
      message: `${principal.displayName} (${principal.ondoId}) isn’t registered as ${WORKSPACE_NOUNS[intended]} on ONDO.`,
      principalName: principal.displayName,
      choices,
      me: me.me,
    };
  }

  if (!active) {
    return {
      kind: "choosing",
      title: WORKSPACE_COPY.chooseTitle,
      principalName: principal.displayName,
      choices,
      me: me.me,
    };
  }

  return {
    kind: "open",
    workspace: active,
    principal,
    capabilities,
    ...describe(active, principal, workspaces),
    screen: workspaceScreen(active.role),
  };
}

/**
 * What a workspace URL resolves to.
 *
 * `redirect` is a variant rather than something the component works out,
 * because every way a URL can be wrong ends the same way — sent somewhere that
 * exists — and which somewhere is a rule, not markup.
 */
export type WorkspaceRouteView =
  | { kind: "loading"; message: string }
  | { kind: "error"; title: string; message: string; unreachable: boolean }
  | { kind: "unprovisioned"; title: string; message: string }
  | { kind: "redirect"; to: string }
  | {
      kind: "open";
      workspace: WorkspaceRef;
      principal: MeResponse["principal"];
      capabilities: MeResponse["capabilities"];
      title: string;
      meta: string;
      canSwitch: boolean;
      screen: WorkspaceScreen;
      /**
       * The caller's membership OF this workspace's organisation, or null on a
       * personal one.
       *
       * Carried because the rail's identity card says something different in an
       * organisation: not "Agent · Verified" but the job title and the rung —
       * "Art Advisor", "Admin · ONDO-5592". Which rung somebody holds is the
       * fact that decides what they may do to the firm's mandates, and a member
       * who cannot see their own has no way to explain a refusal.
       *
       * Matched on the organisation's ONDO id rather than the role, because an
       * account can hold two workspaces of the same role.
       */
      membership: Membership | null;
      /**
       * Null on a workspace with no portal, so no destinations to be at.
       *
       * A string rather than a union: it has already been parsed against THIS
       * portal's spec, and which portal that is only the branch below knows.
       * The screen it is handed to re-narrows it in a switch over its own
       * destinations, which the parser guarantees is total.
       */
      destination: string | null;
      /** The work a `work` destination is about. Null everywhere else. */
      record: string | null;
    };

/**
 * Resolves `/:role/:org/:destination` against what the account actually holds.
 *
 * The URL is a question, never an assertion: it names where to look, and the
 * answer comes from the workspaces the server derived from real grants. A path
 * naming a workspace this account does not hold is therefore not an error — it
 * is a stale bookmark, or access that has since been revoked — so it goes to
 * the chooser, which can say what the account does have.
 */
export function resolveWorkspaceRoute(input: {
  me: MeState;
  role: string | undefined;
  org: string | undefined;
  destination: string | undefined;
  record: string | undefined;
}): WorkspaceRouteView {
  const { me } = input;

  if (me.status === "loading") {
    return { kind: "loading", message: WORKSPACE_COPY.loading };
  }

  if (me.status === "error") {
    return {
      kind: "error",
      title: me.unreachable ? WORKSPACE_COPY.unreachableTitle : WORKSPACE_COPY.errorTitle,
      message: me.message,
      unreachable: me.unreachable,
    };
  }

  if (me.status === "unprovisioned") {
    return {
      kind: "unprovisioned",
      title: WORKSPACE_COPY.unprovisionedTitle,
      message: WORKSPACE_COPY.unprovisioned,
    };
  }

  const { workspaces, principal, capabilities } = me.me;
  const active = matchWorkspace(workspaces, input.role, input.org);
  if (!active) return { kind: "redirect", to: PATHS.workspaces };

  const screen = workspaceScreen(active.role);
  const open = {
    kind: "open",
    workspace: active,
    principal,
    capabilities,
    ...describe(active, principal, workspaces),
    screen,
    membership: membershipFor(active, me.me.memberships),
  } as const;

  // ONE RESOLUTION FOR EVERY PORTAL, and there used to be three.
  //
  // The branch this replaces opened "THE AGENT WORKSPACE HAS EXACTLY ONE
  // DESTINATION, and it is `work`" and admitted nothing else, because the agent
  // screen was a list with no rail and the record it linked to still had to live
  // somewhere. That has stopped being true: an agent portal has a rail like any
  // other, and its destinations are on its spec.
  //
  // What the old branch was RIGHT about survives here unchanged — a work record
  // cannot live at the collector's URL, because an agent may hold no collector
  // workspace at all and `matchWorkspace` would send `/collector/me/work/…` to
  // the chooser. Every portal therefore carries its own `work`, and what renders
  // is the same `WorkRecord` narrowed by the same policies.
  const spec = portalFor(active.role);

  // A workspace with no portal is one screen with no sidebar, so a destination
  // segment names nothing there. Dropped rather than ignored, so the address bar
  // cannot claim a page that is not on screen.
  if (!spec) {
    return input.destination
      ? { kind: "redirect", to: workspacePath(active) }
      : { ...open, destination: null, record: null };
  }

  // Canonical over lenient, for the same reason: `/collector/me` and
  // `/collector/me/nonsense` both belong on the default destination, and both
  // arrive at its own URL rather than rendering it under someone else's.
  const destination = parseDestination(spec, input.destination ?? "");
  if (!destination) {
    return { kind: "redirect", to: workspacePath(active, spec.defaultDestination) };
  }

  // A record segment belongs to exactly one destination. Anywhere else it is
  // DROPPED rather than ignored, for the reason the no-portal case above drops a
  // destination: the address bar must not claim something that is not on screen.
  const record = parseRecord(input.record);
  const rule = recordRule(spec, destination);

  if (rule === "none") {
    return input.record
      ? { kind: "redirect", to: workspacePath(active, destination) }
      : { ...open, destination, record: null };
  }

  // A room id that is not one is dropped to the rooms list rather than followed:
  // the list is a screen in its own right, so there is somewhere sensible to
  // land, and a mistyped id must not silently open the wrong room.
  if (rule === "optional") {
    return input.record && !record
      ? { kind: "redirect", to: workspacePath(active, destination) }
      : { ...open, destination, record };
  }

  // …and a work with no work is not a screen. Back to the portal's own fallback,
  // which is where a record is chosen from — the collection for a collector, the
  // representations list for an agent.
  if (!record) {
    return { kind: "redirect", to: workspacePath(active, spec.recordFallback) };
  }

  return { ...open, destination, record };
}

/**
 * The caller's membership of this workspace's organisation, or null.
 *
 * `status` is not filtered here: `deriveWorkspaces` on the API already refuses
 * to open a workspace for anything but an active membership, so a row reaching
 * this point is one the server stood behind. Filtering again would be the
 * application-level re-check CLAUDE.md forbids — "if a policy is correct it
 * cannot need the help".
 */
export function membershipFor(
  workspace: WorkspaceRef,
  memberships: readonly Membership[],
): Membership | null {
  const ondoId = workspace.organisation?.ondoId;
  if (!ondoId) return null;

  return memberships.find((each) => each.organisation.ondoId === ondoId) ?? null;
}

/** The labels an open workspace carries, wherever it was resolved from. */
function describe(
  active: WorkspaceRef,
  principal: MeResponse["principal"],
  workspaces: readonly WorkspaceRef[],
): { title: string; meta: string; canSwitch: boolean } {
  return {
    title: active.organisation?.displayName ?? WORKSPACE_LABELS[active.role],
    meta:
      `${WORKSPACE_LABELS[active.role]}${active.title ? ` · ${active.title}` : ""}` +
      ` · signed in as ${principal.displayName} (${principal.ondoId})`,
    canSwitch: workspaces.length > 1,
  };
}

/**
 * Which screen a role opens.
 *
 * Read off the portal registry and NOTHING ELSE. A `SCREENS` map stood beside
 * `portalFor` here until the estate portal made it a pure identity over the
 * same five keys — a third copy of which roles are built, whose failure mode
 * was exactly the drift this file's own comment warned about: a role added to
 * `PORTALS` and missed here would have silently opened the placeholder page
 * under a registered portal's URL.
 *
 * Every screen name IS its role name — the `WorkspaceScreen` union restates
 * `RoleKey` plus `placeholder`, and the compiler holds the two in step — so
 * the only real question left is whether a portal is registered, and
 * `portalFor` is the one place that answers it. The `placeholder` fallback
 * stays for the role a future edit registers nowhere: the honest page rather
 * than a crash.
 */
export function workspaceScreen(role: RoleKey): WorkspaceScreen {
  return portalFor(role) ? role : "placeholder";
}

/** Stable across renders, and unique per entity: two galleries are two choices. */
export function workspaceKey(workspace: WorkspaceRef): string {
  return `${workspace.role}:${workspace.organisation?.ondoId ?? "personal"}`;
}

function toChoice(workspace: WorkspaceRef): WorkspaceChoice {
  return {
    key: workspaceKey(workspace),
    name: workspace.organisation?.displayName ?? WORKSPACE_LABELS[workspace.role],
    meta: `${WORKSPACE_LABELS[workspace.role]}${workspace.title ? ` · ${workspace.title}` : ""}`,
    workspace,
  };
}

// ── The empty chooser's door (OND-12) ──────────────────────────────────────

/**
 * Why the chooser is empty, told apart the way the ticket requires.
 *
 * `collections` is what `GET /v1/collections` answered for this account, or
 * null when it has not answered (still loading, or refused). The null case gets
 * a sentence of its own rather than defaulting to "holds nothing": a person who
 * DOES hold a shared collection must not be told to create one because a read
 * had not landed yet, so the claim waits for the fact.
 *
 * "Read-only" is every shared collection at `viewer`. A curator on somebody
 * else's collection is told the same thing in weaker words — they hold access,
 * not a collection of their own, and declaring changes neither.
 */
export type EmptyChooserNote = {
  kind: "nothing" | "shared-read-only" | "shared" | "unknown";
  sentence: string;
};

export function describeEmptyChooser(
  principalName: string,
  collections: readonly CollectionSummary[] | null,
): EmptyChooserNote {
  const say = (template: string, count = 0): string =>
    template
      .replace("{name}", principalName)
      .replace("{count}", plural(count, ["collection", "collections"]));

  if (collections === null) {
    return { kind: "unknown", sentence: say(EMPTY_CHOOSER_COPY.unknown) };
  }
  if (collections.length === 0) {
    return { kind: "nothing", sentence: say(EMPTY_CHOOSER_COPY.nothing) };
  }
  // Strictly `viewer`, so an unexpected level from a newer server reads as
  // "shared" rather than as "read-only" — the weaker claim is the safe one.
  const readOnly = collections.every((collection) => collection.level === "viewer");

  return readOnly
    ? {
        kind: "shared-read-only",
        sentence: say(EMPTY_CHOOSER_COPY.sharedReadOnly, collections.length),
      }
    : { kind: "shared", sentence: say(EMPTY_CHOOSER_COPY.shared, collections.length) };
}

/** One tick box on the declare form, with what the server already holds marked. */
export interface DeclarationOption {
  value: DeclaredMemberType;
  label: string;
  /** Already on the account. Shown ticked and disabled: the route is append-only. */
  held: boolean;
  checked: boolean;
}

/**
 * The form's options, in the canonical order, with the current selection applied.
 *
 * A type the account already declared is held, not offered: `PUT
 * /v1/me/declared-types` cannot remove, so a box that could be unticked would be
 * a lie. On the empty chooser this is almost always none of them, but the
 * chooser is also empty for an account that declared something which earns no
 * workspace of its own (`museum`, say), and that account must see its own answer.
 */
export function declarationOptions(
  held: readonly DeclaredMemberType[],
  selected: readonly DeclaredMemberType[],
): DeclarationOption[] {
  return MEMBERSHIP_TYPES.map((type) => ({
    value: type.value,
    label: type.label,
    held: held.includes(type.value),
    checked: held.includes(type.value) || selected.includes(type.value),
  }));
}

/**
 * Collector, unless the account already is one.
 *
 * The one sensible default: it is the sole route to the collector capacity, and
 * the invited person this door exists for was asked about a work and most often
 * holds pictures of their own. It is a tick, not a decision — the form shows it
 * and lets it be unticked.
 */
export function defaultDeclaration(held: readonly DeclaredMemberType[]): DeclaredMemberType[] {
  return held.includes("collector") ? [] : ["collector"];
}

/** What to send: only what is new. Sending a held type is harmless but says nothing. */
export function newDeclarations(
  held: readonly DeclaredMemberType[],
  selected: readonly DeclaredMemberType[],
): DeclaredMemberType[] {
  return selected.filter((type) => !held.includes(type));
}

/**
 * The claim-your-studio banner's sentence, or null when nothing is waiting —
 * OND-7 over OND-6.
 *
 * Null is the answer for almost everybody, and the banner draws nothing on
 * null: a dormant studio is shown only at the one address it was invited at,
 * and `ondo.claimable_studio()` reads that address off the caller's own
 * session, so there is no id here to get wrong.
 */
export type ClaimStudioNote = {
  eyebrow: string;
  sentence: string;
  ondoId: string;
};

export function describeClaimableStudio(
  studio: ClaimableStudio | null | undefined,
): ClaimStudioNote | null {
  if (!studio) return null;

  return {
    eyebrow: CLAIM_STUDIO_COPY.eyebrow,
    sentence: CLAIM_STUDIO_COPY.sentence
      .replace("{address}", studio.invitedEmail)
      .replace("{waiting}", plural(studio.waiting, CLAIM_STUDIO_COPY.waiting)),
    ondoId: studio.ondoId,
  };
}

/**
 * Where a successful claim lands: the studio's own workspace in the profile
 * the server just returned, or the chooser when it is not there.
 *
 * Found by the studio's ONDO id rather than by role, because the account may
 * already hold an estate workspace of its own and the claim must land on the
 * one it just took. Null never means "guess" — the chooser is a true answer
 * when the server's profile does not name the workspace.
 */
export function studioWorkspaceFor(me: MeResponse, ondoId: string): WorkspaceRef | null {
  return me.workspaces.find((w) => w.organisation?.ondoId === ondoId) ?? null;
}

/** One seat invitation, as the banner draws it — OND-24. */
export type SeatInvitationNote = {
  invitationId: string;
  orgOndoId: string;
  /** "Hauser & Wirth has invited you to a seat…" — the whole offer, in a sentence. */
  sentence: string;
};

/**
 * The seat invitations waiting at this account's own address, as sentences —
 * OND-24, and `describeClaimableStudio`'s shape one ticket along.
 *
 * AN EMPTY LIST IS THE ANSWER FOR ALMOST EVERYBODY and the banner draws nothing
 * on it. `ondo.my_seat_invitations()` reads the caller's own confirmed address
 * off their session, so there is no id here to get wrong and nothing a caller
 * could ask about anybody else.
 *
 * THE SENTENCE NAMES THE RUNG IN WORDS, because that is the one fact the reader
 * cannot know from outside and the one that decides whether to accept: "an
 * administrator, which can add and remove colleagues" is a different proposition
 * from "a viewer". The frozen rung on the row IS what accepting will give them —
 * `20261014092200` decision 4 — so the sentence is not an approximation.
 *
 * It does NOT say who else is at the firm, what the firm holds, or whether the
 * inviter is senior. None of that is on the wire, deliberately: an invitation is
 * addressed to an address, and an address can be mistyped.
 */
export function describeSeatInvitations(
  invitations: readonly MySeatInvitation[] | null | undefined,
): SeatInvitationNote[] {
  return (invitations ?? []).map((invitation) => ({
    invitationId: invitation.invitationId,
    orgOndoId: invitation.orgOndoId,
    sentence: SEAT_INVITATION_COPY.sentence
      .replace("{firm}", invitation.orgName)
      .replace("{rung}", SEAT_INVITATION_RUNGS[invitation.level])
      .replace("{inviter}", invitation.invitedByName)
      .replace("{address}", invitation.invitedEmail),
  }));
}

/**
 * Where a successful acceptance lands — `studioWorkspaceFor` by another name,
 * and it IS that function rather than a copy.
 *
 * Kept as a separate export so the two call sites read as what they are, and
 * because the day either flow needs a different rule the other must not change
 * with it. Found by the firm's ONDO id rather than by role, because the account
 * may already hold a workspace of the same kind and the acceptance must land on
 * the firm it just joined.
 */
export function seatWorkspaceFor(me: MeResponse, ondoId: string): WorkspaceRef | null {
  return studioWorkspaceFor(me, ondoId);
}
